type AuthStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

function createMemoryStorage(): AuthStorage {
  const store: Record<string, string> = {};
  return {
    getItem: (key) => store[key] ?? null,
    setItem: (key, value) => {
      store[key] = value;
    },
    removeItem: (key) => {
      delete store[key];
    },
  };
}

function isLocalStorageAvailable(): boolean {
  try {
    if (typeof globalThis.localStorage !== "object") return false;
    const probe = `__storage_probe_${Math.random()}`;
    globalThis.localStorage.setItem(probe, probe);
    globalThis.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/**
 * Supabase auth storage that falls back to in-memory when localStorage is blocked
 * (e.g. Safari iOS private mode, strict tracking prevention, embedded WebViews).
 */
export function createSafeAuthStorage(): AuthStorage {
  const memory = createMemoryStorage();

  if (!isLocalStorageAvailable()) {
    return memory;
  }

  return {
    getItem(key) {
      try {
        return globalThis.localStorage.getItem(key);
      } catch {
        return memory.getItem(key);
      }
    },
    setItem(key, value) {
      try {
        globalThis.localStorage.setItem(key, value);
      } catch {
        memory.setItem(key, value);
      }
    },
    removeItem(key) {
      try {
        globalThis.localStorage.removeItem(key);
      } catch {
        memory.removeItem(key);
      }
    },
  };
}
